import {skipToken} from '@reduxjs/toolkit/query';
import {FormControl, FormField} from 'platform/components';
import {Text, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetMakeModelWithMakeQuery} from '@dms/api/sales';
import i18n, {FALLBACK_LANGUAGE} from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {toSelectableMakeOptions} from 'features/vehicle-catalogue';

import {CustomCatalogueMakeGroup} from '../utils/groupUsageByMake';
import {KEEP_ORIGINAL_MODEL_FAMILY, RemapMakeFormValues} from '../utils/remapMakeFormSchema';
import {ModelFamilyMappingFields} from './ModelFamilyMappingFields';
import {RemapFormFooter} from './RemapFormFooter';

interface RemapMakeFormContentProps extends RequiredTestIdProps {
  control: FormControl<RemapMakeFormValues>;
  formApi: UseFormReturn<RemapMakeFormValues>;
  group: CustomCatalogueMakeGroup;
}

export function RemapMakeFormContent(props: RemapMakeFormContentProps) {
  const targetMake = useWatch({control: props.control, name: 'targetMake'});
  const modelFamilyMapping = useWatch({control: props.control, name: 'modelFamilyMapping'});

  // Same lang argument as the vehicle form hooks, so the cached makes list is shared with them.
  const {data: makeCatalogue, isFetching: isMakeCatalogueFetching} = useGetMakeModelWithMakeQuery({
    vehicleType: props.group.vehicleType,
    lang: [i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
  });

  // currentData, not data: while a newly picked make is loading, `data` still holds the previous
  // make's models, which would let the user submit a cross-make mapping.
  const {currentData: modelCatalogue, isFetching: isModelCatalogueFetching} =
    useGetMakeModelWithMakeQuery(
      isNilOrEmpty(targetMake)
        ? skipToken
        : {
            vehicleType: props.group.vehicleType,
            make: targetMake,
            lang: [i18n?.resolvedLanguage ?? FALLBACK_LANGUAGE],
          }
    );

  // Guarded on the value actually changing: re-picking the already selected make fires onChange too,
  // and that must not throw away targets the user has filled in.
  const handleTargetMakeChange = (newTargetMake: string | number | null) => {
    if (newTargetMake === targetMake) {
      return;
    }

    // Keep-original names no model, so it stays valid under any make.
    modelFamilyMapping.forEach((item, index) => {
      if (item.targetModelFamily !== KEEP_ORIGINAL_MODEL_FAMILY) {
        props.formApi.setValue(`modelFamilyMapping.${index}.targetModelFamily`, '');
      }
    });
  };

  const isSubmitDisabled =
    isNilOrEmpty(targetMake) ||
    modelFamilyMapping.some((item) => isNilOrEmpty(item.targetModelFamily));

  return (
    <VStack spacing={4}>
      <Text size="small">
        {i18n.t('entity.customCatalogue.replaceMakeDialog.descriptionKeepOriginal')}
      </Text>

      <FormField
        control={props.control}
        name="targetMake"
        type="choice"
        label={i18n.t('entity.customCatalogue.labels.newMake')}
        options={toSelectableMakeOptions(makeCatalogue)}
        isLoading={isMakeCatalogueFetching}
        onChange={handleTargetMakeChange}
        isRequired
        isNotClearable
        menuInPortal
        data-testid={suffixTestId('targetMake', props)}
      />

      <ModelFamilyMappingFields
        control={props.control}
        models={props.group.models}
        modelCatalogue={modelCatalogue}
        isDisabled={isNilOrEmpty(targetMake) || isModelCatalogueFetching}
        isLoading={isModelCatalogueFetching}
        data-testid={props['data-testid']}
      />

      <RemapFormFooter
        control={props.control}
        isSubmitDisabled={isSubmitDisabled}
        data-testid={props['data-testid']}
      />
    </VStack>
  );
}
